import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
  isActive?: boolean;
}

interface NavigationState {
  breadcrumbs: BreadcrumbItem[];
  currentSection: string;
  history: string[];
}

export const useNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [navigationState, setNavigationState] = useState<NavigationState>({
    breadcrumbs: [],
    currentSection: '',
    history: []
  });

  // Маппинг путей к разделам
  const sectionMap: { [key: string]: { title: string; icon?: React.ReactNode; parent?: string } } = {
    '/': { title: 'Главная', icon: '🏠' },
    '/calculator': { title: 'Калькулятор дозировок', icon: '🧮' },
    '/vaccination': { title: 'Календарь вакцинации', icon: '💉' },
    '/atlas': { title: 'Атлас сыпей', icon: '📸' },
    '/emergency-care': { title: 'Неотложная помощь', icon: '🚨' },
    '/children-up-to-year': { title: 'Дети до года', icon: '👶' },
    '/growth-charts': { title: 'Графики роста и веса', icon: '📈' },
    '/orders': { title: 'Приказы и нормативы', icon: '📋' },
    '/ai-assistant': { title: 'ИИ-помощник', icon: '🤖' },
    '/about-data': { title: 'О данных', icon: 'ℹ️' },
    '/privacy-policy': { title: 'Политика конфиденциальности', icon: '🔒' },
    '/security-compliance': { title: 'Безопасность и соответствие', icon: '🛡️' },
  };

  // Обновление хлебных крошек при изменении маршрута
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];

    // Добавляем главную страницу
    breadcrumbs.push({
      label: 'Главная',
      path: '/',
      icon: '🏠',
      isActive: location.pathname === '/'
    });

    // Строим хлебные крошки на основе текущего пути
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const section = sectionMap[currentPath];
      
      if (section) {
        breadcrumbs.push({
          label: section.title,
          path: currentPath,
          icon: section.icon,
          isActive: index === pathSegments.length - 1
        });
      }
    });

    // Обновляем историю навигации
    setNavigationState(prev => {
      const newHistory = [...prev.history];
      if (prev.currentSection !== location.pathname) {
        newHistory.unshift(location.pathname);
        // Ограничиваем историю 10 элементами
        if (newHistory.length > 10) {
          newHistory.pop();
        }
      }

      return {
        breadcrumbs,
        currentSection: location.pathname,
        history: newHistory
      };
    });
  }, [location.pathname]);

  // Функция навигации с обновлением истории
  const navigateTo = (path: string) => {
    navigate(path);
  };

  // Функция возврата к предыдущему разделу
  const goBack = () => {
    if (navigationState.history.length > 1) {
      const previousPath = navigationState.history[1];
      navigate(previousPath);
    } else {
      navigate('/');
    }
  };

  // Функция получения связанных разделов
  const getRelatedSections = (currentPath: string): string[] => {
    const relatedMap: { [key: string]: string[] } = {
      '/calculator': ['/children-up-to-year', '/emergency-care', '/ai-assistant'],
      '/vaccination': ['/children-up-to-year', '/orders', '/growth-charts'],
      '/atlas': ['/emergency-care', '/children-up-to-year', '/ai-assistant'],
      '/emergency-care': ['/calculator', '/atlas', '/ai-assistant'],
      '/children-up-to-year': ['/calculator', '/vaccination', '/atlas', '/growth-charts'],
      '/growth-charts': ['/children-up-to-year', '/calculator'],
      '/orders': ['/vaccination', '/children-up-to-year'],
      '/ai-assistant': ['/calculator', '/emergency-care', '/atlas'],
    };

    return relatedMap[currentPath] || [];
  };

  // Функция поиска по разделам
  const searchSections = (query: string) => {
    const results = Object.entries(sectionMap)
      .filter(([path, section]) => 
        section.title.toLowerCase().includes(query.toLowerCase())
      )
      .map(([path, section]) => ({
        path,
        title: section.title,
        icon: section.icon
      }));

    return results;
  };

  return {
    breadcrumbs: navigationState.breadcrumbs,
    currentSection: navigationState.currentSection,
    history: navigationState.history,
    navigateTo,
    goBack,
    getRelatedSections,
    searchSections,
    sectionMap
  };
};
